import Skeleton from '@mui/material/Skeleton';
import { Theme } from '@mui/material/styles/createTheme';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import { ISlideComponentProps, Slide, SlideEvent } from '../Slide';

const useStyles = makeStyles((theme: Theme) => ({
  queueSlideContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,

    transition: 'opacity 1000ms ease-out',
    opacity: 0,
  },
  queueSlideContainerVisible: {
    opacity: 1,
  },
  sideBySideContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  leftContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    marginRight: theme.spacing(2),
    minWidth: 500,
  },
  rightContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    flex: 1,
    marginRight: theme.spacing(4),
  },
  bottomContainer: {
    display: 'flex',
    marginTop: 0,
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    marginBottom: theme.spacing(4),
    padding: 0,
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: 5,
    textAlign: 'center',
    justifyContent: 'center',
    minHeight: 185 + theme.spacing(2),
  },
  bannerContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    minHeight: 130,
    minWidth: 500,

    [theme.breakpoints.down('lg')]: {
      minHeight: 160,
    },
  },
  bannerSkeletonContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    minHeight: 90,
    minWidth: 325,
  },
  bannerSkeleton: {
    minHeight: 90,
    minWidth: 325,
    borderRadius: 5,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  availabilityLevelContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    marginLeft: theme.spacing(4),
    padding: 0,
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: 5,
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
  },
  availabilityLevelSkeleton: {
    width: '100%',
    height: '100%',
    borderRadius: 5,
  },
  memberAvailabilityContainer: {
    padding: 0,
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: 5,
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    minHeight: 130,

    [theme.breakpoints.down('lg')]: {
      height: 160,
    },
  },
  memberAvailabilitySkeleton: {
    width: '100%',
    height: '100%',
    borderRadius: 5,
  },
  inboundCallTimeContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: 0,
    backgroundColor: '#fff',
    border: '1px solid #eee',
    borderRadius: 5,
    textAlign: 'center',
    minHeight: 250 + theme.spacing(2),
  },
  inboundCallTimeSkeleton: {
    width: '100%',
    height: '100%',
    borderRadius: 5,
  },
  inboundCallsContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: 0,
    minHeight: 167 + theme.spacing(4),
  },
  inboundCallsSkeleton: {
    width: '100%',
    height: '100%',
    borderRadius: 5,
  },
}));

export default function SkeletonLoading({
  slideObservable,
}: ISlideComponentProps) {
  const classes = useStyles();

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = (_slide: Slide, event: SlideEvent) => {
      if (event === SlideEvent.animateInStarted) {
        setIsVisible(true);
      } else if (event === SlideEvent.animateOutStarted) {
        setIsVisible(false);
      }
    };

    slideObservable.observe(observer);

    return () => slideObservable.unobserve(observer);
  }, [slideObservable]);

  return (
    <div
      className={`${classes.queueSlideContainer} ${
        isVisible ? classes.queueSlideContainerVisible : ''
      }`}
    >
      <div className={classes.sideBySideContainer}>
        <div className={classes.leftContainer}>
          <div className={classes.bannerContainer}>
            <div className={classes.bannerSkeletonContainer}>
              <Skeleton
                className={classes.bannerSkeleton}
                variant="rectangular"
              />
            </div>
          </div>
          <div className={classes.availabilityLevelContainer}>
            <Skeleton
              className={classes.availabilityLevelSkeleton}
              variant="rectangular"
            />
          </div>
        </div>
        <div className={classes.rightContainer}>
          <div className={classes.memberAvailabilityContainer}>
            <Skeleton
              className={classes.memberAvailabilitySkeleton}
              variant="rectangular"
            />
          </div>
          <div className={classes.inboundCallTimeContainer}>
            <Skeleton
              className={classes.inboundCallTimeSkeleton}
              variant="rectangular"
            />
          </div>
        </div>
      </div>
      <div className={classes.bottomContainer}>
        <div className={classes.inboundCallsContainer}>
          <Skeleton
            className={classes.inboundCallsSkeleton}
            variant="rectangular"
          />
        </div>
      </div>
    </div>
  );
}
